import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getClient, setToken } from '../../../utils/http-client'

function SSOCallback() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(true)

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code')
      const state = searchParams.get('state')
      const error = searchParams.get('error')
      const errorDescription = searchParams.get('error_description')

      // Handle error from SSO provider
      if (error) {
        setError(errorDescription || error || 'Authentication failed')
        setIsProcessing(false)
        return
      }

      // Validate we have required parameters
      if (!code || !state) {
        console.error('Missing callback parameters:', {
          code: !!code,
          state: !!state,
        })
        setError('Invalid callback parameters. Please try logging in again.')
        setIsProcessing(false)
        return
      }

      console.log('SSO Callback - Processing with:', {
        code: code?.substring(0, 10) + '...',
        state: state?.substring(0, 10) + '...',
        fullState: state, // Log full state for debugging
      })

      try {
        // Exchange code for token with backend
        const client = getClient({ reloadOnAuthError: false })
        const response = await client.post('auth/sso/callback/', {
          code,
          state,
        })

        if (response.data.token) {
          // Store the authentication token using the utility function
          // This will reload the page and update the auth context
          setToken(response.data.token, false)

          // Store user data if provided
          if (response.data.user) {
            localStorage.setItem('user', JSON.stringify(response.data.user))
          }

          // Redirect to dashboard or intended destination
          const redirectTo = localStorage.getItem('redirectAfterLogin') || '/'
          localStorage.removeItem('redirectAfterLogin')

          // Navigate to the app
          window.location.href = redirectTo
        } else {
          throw new Error('No authentication token received')
        }
      } catch (err: any) {
        console.error('SSO callback error:', err)

        // Check for specific error cases
        const errorMessage =
          err.response?.data?.error ||
          err.message ||
          'Failed to complete SSO authentication'

        if (
          errorMessage.includes('Invalid or expired state') ||
          errorMessage.includes('state parameter') ||
          err.response?.status === 401
        ) {
          // State expired or invalid - redirect to SSO login to start fresh
          setError('Your session has expired. Redirecting to login...')
          setTimeout(() => {
            navigate('/sso-login')
          }, 2000)
        } else {
          // Other errors
          setError(errorMessage + '. Please try again.')
        }

        setIsProcessing(false)
      }
    }

    handleCallback()
  }, [searchParams, navigate])

  if (isProcessing) {
    return (
      <div className="flex justify-center items-center min-h-screen p-5 bg-gray-50">
        <div className="bg-white rounded-lg p-10 shadow-md max-w-sm w-full text-center">
          <h2 className="text-xl mb-6 text-gray-800">Completing sign in...</h2>
          <div className="inline-block w-10 h-10 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin my-5"></div>
          <p className="text-gray-600 text-sm mt-4">
            Please wait while we complete your authentication.
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen p-5 bg-gray-50">
        <div className="bg-white rounded-lg p-10 shadow-md max-w-sm w-full text-center">
          <h2 className="text-xl mb-6 text-red-600">Authentication Failed</h2>
          <p className="text-red-600 text-sm mb-6">{error}</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <button
              onClick={() => navigate('/sso-login')}
              className="px-6 py-2.5 bg-blue-500 text-white border-none rounded text-sm cursor-pointer transition-colors duration-200 hover:bg-blue-700"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2.5 bg-blue-500 text-white border-none rounded text-sm cursor-pointer transition-colors duration-200 hover:bg-blue-700"
            >
              Use Email/Password
            </button>
          </div>
        </div>
      </div>
    )
  }

  return null
}

export default SSOCallback
